import React from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Lock, Sparkles } from "lucide-react";
import api from "@/lib/api";
import { EmptyState, Loading } from "@/components/common/Loading";
import { OaOverview } from "@/components/admin/otterassist/OaOverview";
import { OaAssists } from "@/components/admin/otterassist/OaAssists";
import { OaReports } from "@/components/admin/otterassist/OaReports";
import { OaAccess } from "@/components/admin/otterassist/OaAccess";
import { OaControls } from "@/components/admin/otterassist/OaControls";
import { OaAnalytics } from "@/components/admin/otterassist/OaAnalytics";
import { OaAudit } from "@/components/admin/otterassist/OaAudit";

// Otter Assist admin console — ONE section inside the existing Admin Console, with secondary views
// in the URL (`?view=`) so deep links, Back/Forward and reload all work.
//
// The backend re-checks every capability on every call; this file only decides what to render.

const VIEWS = [
  { key: "overview", label: "Overview", cap: "health" },
  { key: "active", label: "Active Assists", cap: "assists" },
  { key: "historical", label: "Historical", cap: "assists" },
  { key: "reports", label: "Reports", cap: "moderate" },
  { key: "access", label: "Access & Overrides", cap: "access" },
  { key: "controls", label: "Controls", cap: "controls_view" },
  { key: "analytics", label: "Analytics", cap: "analytics" },
  { key: "audit", label: "Audit", cap: "audit" },
];

export default function OtterAssistAdmin() {
  const [params, setParams] = useSearchParams();
  const { data, isLoading, isError } = useQuery({
    queryKey: ["oa-admin-me"],
    queryFn: async () => (await api.get("/admin/otter-assist/me")).data,
    retry: false,
  });

  if (isLoading) return <Loading />;
  if (isError || !data) {
    return <EmptyState icon={Lock} title="Otter Assist management is unavailable"
      description="The Otter Assist admin console is switched off for this deployment."
      testId="oa-admin-unavailable" />;
  }

  const caps = new Set(data.capabilities || []);
  const allowed = VIEWS.filter((v) => caps.has(v.cap));
  const requested = params.get("view");
  const view = allowed.some((v) => v.key === requested) ? requested
    : (allowed[0]?.key || "analytics");

  const setView = (key) => {
    const next = new URLSearchParams(params);
    next.set("view", key);
    setParams(next, { replace: true });
  };

  return (
    <div data-testid="oa-admin-console">
      <div className="flex flex-wrap items-center gap-2">
        <span className="inline-flex items-center gap-1 rounded-full bg-teal-700 px-2 py-0.5 text-[9px] font-[700] uppercase tracking-[0.08em] text-white">
          <Sparkles className="h-2.5 w-2.5" /> Otter Assist
        </span>
        <span className="rounded bg-secondary px-2 py-0.5 text-[10px] font-[700] uppercase tracking-wider text-muted-foreground"
          data-testid="oa-admin-level">
          {String(data.level).replace(/_/g, " ")}
        </span>
      </div>

      <div className="mt-3 flex flex-wrap gap-1.5 border-b border-border pb-px">
        {allowed.map((v) => (
          <button key={v.key} type="button" onClick={() => setView(v.key)}
            data-testid={`oa-admin-view-${v.key}`}
            className={`rounded-t-lg px-3 py-1.5 text-[13px] font-[560] transition-colors ${
              view === v.key ? "bg-teal-50 text-teal-700"
                : "text-foreground/60 hover:bg-secondary hover:text-foreground"}`}>
            {v.label}
          </button>
        ))}
      </div>

      <div className="mt-5">
        {view === "overview" && <OaOverview />}
        {view === "active" && <OaAssists group="active" />}
        {view === "historical" && <OaAssists group="historical" />}
        {view === "reports" && <OaReports />}
        {view === "access" && <OaAccess />}
        {view === "controls" && <OaControls />}
        {view === "analytics" && <OaAnalytics />}
        {view === "audit" && <OaAudit />}
      </div>
    </div>
  );
}
